import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CleanTextResult, CleanTextSummary, TypeId } from './api';
import { MarkdownView } from './MarkdownView';
import { findOccurrences } from './placeholders';
import {
  DocumentHeader,
  MissedTerm,
  Notices,
  OccurrenceStepper,
  ResultActs,
  TypeChips,
  markdownName,
  saveText,
  summaryTypes,
} from './ResultParts';

export { markdownName };

interface ResultViewProps {
  readonly result: CleanTextResult;
  readonly sourceName: string;
  readonly onNewDocument: () => void;
  readonly onAddTerm: () => void;
  readonly onReprocessLanguage: (language: 'it' | 'en') => void;
}

/** The marked-up occurrences of the chosen type and the stepper position over them. */
function useOccurrenceStepper(markdown: string, mode: 'rendered' | 'raw') {
  const [chosen, setChosen] = useState<TypeId | null>(null);
  const [position, setPosition] = useState(0);
  const textRef = useRef<HTMLDivElement | null>(null);

  const occurrences = useMemo(() => findOccurrences(markdown), [markdown]);
  const stops = useMemo(
    () => (chosen ? occurrences.filter((o) => o.type === chosen) : []),
    [occurrences, chosen],
  );
  const current = stops[position]?.index ?? null;

  useEffect(() => {
    if (current === null) return;
    textRef.current
      ?.querySelector(`[data-placeholder="${current}"]`)
      ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [current, mode]);

  const choose = (id: TypeId): void => {
    setPosition(0);
    setChosen((now) => (now === id ? null : id));
  };
  const step = (by: 1 | -1): void =>
    setPosition((p) => (stops.length === 0 ? 0 : (p + by + stops.length) % stops.length));
  return { chosen, position, stops, current, textRef, choose, step };
}

function SummaryPanel(props: {
  readonly summary: CleanTextSummary;
  readonly stepper: ReturnType<typeof useOccurrenceStepper>;
  readonly onAddTerm: () => void;
  readonly onReprocessLanguage: (language: 'it' | 'en') => void;
}) {
  const { t } = useTranslation('tools');
  const { summary, stepper } = props;
  const types = summaryTypes(summary.countsByType);
  const total = types.reduce((sum, x) => sum + x.count, 0);
  const typeLabel = (id: string): string => t(`cleanText.types.${id}`, { defaultValue: id });
  return (
    <aside
      aria-label={t('cleanText.result.summaryLabel')}
      className="order-first space-y-3 rounded-xl border border-border bg-card p-4 lg:col-start-2 lg:row-start-1"
    >
      <h3 className="text-base font-bold">
        {total > 0
          ? t('cleanText.result.replaced', { count: total })
          : t('cleanText.result.nothingFound')}
      </h3>
      <TypeChips
        types={types}
        chosen={stepper.chosen}
        typeLabel={typeLabel}
        onChoose={stepper.choose}
      />
      {stepper.chosen && (
        <OccurrenceStepper
          typeName={typeLabel(stepper.chosen)}
          stops={stepper.stops}
          position={stepper.position}
          onStep={stepper.step}
        />
      )}
      <Notices
        summary={summary}
        typeLabel={typeLabel}
        onReprocessLanguage={props.onReprocessLanguage}
      />
      <MissedTerm onAddTerm={props.onAddTerm} />
    </aside>
  );
}

export function ResultView({
  result,
  sourceName,
  onNewDocument,
  onAddTerm,
  onReprocessLanguage,
}: ResultViewProps) {
  const { markdown, summary } = result;
  const [mode, setMode] = useState<'rendered' | 'raw'>('rendered');
  const [copied, setCopied] = useState<'done' | 'failed' | null>(null);
  const stepper = useOccurrenceStepper(markdown, mode);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied('done');
    } catch {
      setCopied('failed');
    }
  };

  return (
    <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
      <SummaryPanel
        summary={summary}
        stepper={stepper}
        onAddTerm={onAddTerm}
        onReprocessLanguage={onReprocessLanguage}
      />
      <section className="min-w-0 rounded-xl border border-border bg-card p-4 sm:p-5 lg:col-start-1 lg:row-span-2 lg:row-start-1">
        <DocumentHeader
          name={markdownName(sourceName)}
          pages={summary.pages}
          mode={mode}
          onMode={setMode}
        />
        <div ref={stepper.textRef} data-testid="clean-text-body">
          <MarkdownView
            markdown={markdown}
            mode={mode}
            highlight={{ type: stepper.chosen, current: stepper.current }}
          />
        </div>
      </section>
      <ResultActs
        copied={copied}
        onCopy={() => void copy()}
        onDownload={() => saveText(markdown, markdownName(sourceName))}
        onNewDocument={onNewDocument}
      />
    </div>
  );
}
